import React, { useEffect, useState } from 'react';
import {
	Grid,
	Paper,
	styled,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	IconButton as IconBtn,
	TablePagination,
} from '@mui/material';
import SuperAdminSideNavigationBar from '../../components/SuperAdminSideNavigationBar';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import AlertDialog from '../../components/AlertDialog';
import { DeleteIcon, EditIcon } from '../../assets/icons/Icons';
import { Link } from 'react-router-dom';
import {
	deleteUser,
	fetchAllUsers,
	updateUser,
} from '../../services/UserService';
import { getAccessToken } from '../../Utils/AuthUtil';
import { toast } from 'react-toastify';
import UserControlPopup from '../../components/UserControlPopup';
import { USER_TYPES, WEB_ACCOUNT } from '../../Utils/Enums';

const INITIAL_USER_STATE = {
	id: '',
	name: '',
	userType: '',
	userAccountType: '',
};

function UsersDashboard(props) {
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const [userData, setUserData] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedFeatures, setSelectedFeatures] = useState([]);
	const [selectedUser, setSelectedUser] = useState(INITIAL_USER_STATE);
	const [loading, setLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [page, setPage] = useState(0);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	useEffect(() => {
		getAllUsers();
	}, []);

	async function getAllUsers() {
		const data = await fetchAllUsers(token);
		setUserData(data.content);
	}

	async function removeUser(id) {
		const response = await deleteUser(id, token);
		if (response.message === 'Success') {
			getAllUsers();
			toast.success('User removed Successfully');
		} else {
			toast.success(response.message);
		}
	}

	async function updateUserDetails() {
		const userType = USER_TYPES.find(
			item => item.name === selectedUser.userType,
		);

		const body = {
			userAccountVersion: userType.key,
			featuresTypes: selectedFeatures,
		};
		const response = await updateUser(selectedUser.id, token, body);
		if (response.message === 'Success') {
			getAllUsers();
			toast.success('User updated Successfully');
			setIsModalOpen(false);
		} else {
			toast.success(response.message);
		}
	}

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	function onSelectFeature(key) {
		if (selectedFeatures.includes(key)) {
			const clone = selectedFeatures.filter(item => item != key);
			setSelectedFeatures(clone);
		} else {
			setSelectedFeatures(prevState => [...prevState, key]);
		}
	}

	function onOpenUserControls(id, name, type, features, userAccountType) {
		const userType = USER_TYPES.find(item => item.key === type).name;
		setSelectedUser({ id, name, userType, userAccountType });
		setSelectedFeatures(features);
		setIsModalOpen(true);
	}

	function getUserName(label) {
		if (!label) {
			return 'N/A';
		}

		const type = USER_TYPES.find(item => item.key === label);
		return type.name;
	}

	function onChangeUserType(key) {
		setSelectedUser(prevState => ({ ...prevState, userType: key }));
	}

	function onCloseModal() {
		setIsModalOpen(false);
		setSelectedUser(INITIAL_USER_STATE);
	}

	return (
		<>
			<SuperAdminSideNavigationBar />
			<div style={mainDivStyle}>
				<Grid container paddingLeft={'20px'} paddingRight={'20px'}>
					<Grid item paddingLeft="30px">
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							App users
						</Typography>
					</Grid>
					<Grid container paddingLeft="30px">
						<TableContainer
							component={Paper}
							style={{ boxShadow: 'none', marginTop: '30px' }}>
							<Table
								sx={{ minWidth: 700 }}
								aria-label="customized table">
								<TableHead>
									<TableRow>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Username
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Work email
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Account Created Platform
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												User type
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Created on
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Actions
											</Typography>
										</StyledTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{userData && userData.length > 0 ? (
										userData
											.slice(
												page * rowsPerPage,
												page * rowsPerPage +
													rowsPerPage,
											)
											.map((user, index) => {
												return (
													<StyledTableRow key={index}>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{user.username}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{user.email}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{user.accountType
																	? user.accountType
																	: 'N/A'}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{getUserName(
																	user.userAccountVersion,
																)}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{new Date(
																	user.joinedDate,
																)
																	.getDate()
																	.toString()
																	.padStart(
																		2,
																		'0',
																	) +
																	'/' +
																	(
																		new Date(
																			user.joinedDate,
																		).getMonth() +
																		1
																	)
																		.toString()
																		.padStart(
																			2,
																			'0',
																		) +
																	'/' +
																	new Date(
																		user.joinedDate,
																	)
																		.getFullYear()
																		.toString()}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<IconBtn
																sx={{
																	marginRight:
																		'3px',
																}}
																onClick={() =>
																	onOpenUserControls(
																		user.id,
																		user.username,
																		user.userAccountVersion,
																		user.featuresTypes,
																		user.accountType,
																	)
																}>
																<EditIcon />
															</IconBtn>
															<AlertDialog
																id={user.id}
																title={
																	'Remove User'
																}
																bodyContent={
																	'Removing user will permanently delete all the data of this user.'
																}
																icon={() => (
																	<DeleteIcon />
																)}
																deleteData={
																	removeUser
																}
																cancelBtnText={
																	'Keep the user'
																}
																deleteBtnText={
																	'Remove user'
																}
															/>
														</StyledTableCell>
													</StyledTableRow>
												);
											})
									) : (
										<StyledTableRow>
											<StyledTableCell
												colSpan={6}
												align="center">
												<Typography variant="tableBody">
													No data available
												</Typography>
											</StyledTableCell>
										</StyledTableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[]}
							component="div"
							count={userData && userData.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Grid>
				</Grid>
			</div>
			<UserControlPopup
				isModalOpen={isModalOpen}
				selectedData={selectedFeatures}
				onSelectItem={onSelectFeature}
				selectedUser={selectedUser}
				onClickBtn={updateUserDetails}
				loading={loading}
				onChangeUserType={onChangeUserType}
				onClose={onCloseModal}
			/>
		</>
	);
}

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: '#FFFFFF',
		color: 'rgba(0, 0, 0, 0.5)',
		paddingTop: '20px',
		fontSize: 12,
		fontWeight: 600,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 12,
		fontWeight: 400,
		color: '#000000',
		padding: '10px',
	},
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:nth-of-type(odd)': {
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #C2C9D1',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

export default UsersDashboard;
